import { useCallback, useState } from 'react';

import { DEBOUNCE_TIME } from '@core/constants/common';
import Enums from '@core/types/api/Enums';
import { hasStringValue } from '@core/utils/equality';
import { useDebouncedCallback } from '@web/hooks/useDebouncedCallback';
import { useInvalidateCache } from '@web/hooks/useInvalidateCache';
import { useSlabSocket } from '@web/hooks/useSlabSocket';

type NotificationsSocket = {
  pendingArrivalIds: ReadonlySet<string>;
  markArrivalShown: (id: string) => void;
  clearPendingArrivals: () => void;
};

export const useNotificationsSocket = (isPanelOpen: boolean): NotificationsSocket => {
  const { invalidateCache } = useInvalidateCache();
  const [pendingArrivalIds, setPendingArrivalIds] = useState<ReadonlySet<string>>(new Set());

  const invalidateNotifications = useCallback(
    (): void =>
      invalidateCache([
        'GET notifications',
        'GET notification stats',
        'GET reminder user setting summaries',
      ]),
    [invalidateCache],
  );
  // A reminder batch commits many notifications for one user at once; refetch once per burst.
  const refetch = useDebouncedCallback(invalidateNotifications, DEBOUNCE_TIME);

  const markArrivalShown = useCallback((id: string): void => {
    setPendingArrivalIds((current) => {
      if (!current.has(id)) {
        return current;
      }
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);
  const clearPendingArrivals = useCallback((): void => setPendingArrivalIds(new Set()), []);

  useSlabSocket(
    () => [
      {
        method: Enums.WsEvent.NotificationCreated,
        action: (payload): void => {
          refetch();
          const { id } = payload as { id?: string };
          if (isPanelOpen && hasStringValue(id)) {
            setPendingArrivalIds((current) => new Set(current).add(id));
          }
        },
      },
      { method: Enums.WsEvent.NotificationsChanged, action: refetch },
      { method: Enums.WsEvent.Reconnected, action: refetch },
    ],
    [isPanelOpen, refetch],
  );

  return { pendingArrivalIds, markArrivalShown, clearPendingArrivals };
};
